import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, PenLine } from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "./AdminShell";
import { AiAssistant } from "./AiAssistant";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Markdown } from "../../components/Markdown";

const CATEGORIES = ["Guides", "VPN News", "Security Tips", "Deals"];
const TAGS = ["Industry", "Streaming", "Privacy", "Product", "Technology"];

const EMPTY_POST = {
  title: "", slug: "", category: "Guides", excerpt: "", content: "",
  meta_title: "", meta_description: "", author: "VPNReviews Editorial Team",
  reading_time: "5 min read", date: "", published: false,
};
const EMPTY_NEWS = {
  title: "", slug: "", tag: "Industry", body: "", cta_label: "", cta_url: "", date: "", published: false,
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#4687FF] focus:outline-none";

const AdminEditor = ({ type }) => {
  const isPost = type === "posts";
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(isPost ? EMPTY_POST : EMPTY_NEWS);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/admin/${type}/${id}`)
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id, isNew, type]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (publish) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = { ...form, published: publish };
    try {
      if (isNew) {
        await api.post(`/admin/${type}`, payload);
      } else {
        await api.put(`/admin/${type}/${id}`, payload);
      }
      toast.success(publish ? "Published!" : "Saved as draft");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const mdValue = isPost ? form.content : form.body;
  const mdKey = isPost ? "content" : "body";

  if (loading)
    return (
      <AdminShell>
        <p className="py-20 text-center text-sm text-slate-500">Loading…</p>
      </AdminShell>
    );

  return (
    <AdminShell>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate("/admin")}
          data-testid="editor-back-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#4687FF]"
        >
          <ArrowLeft size={15} /> Back to dashboard
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => save(false)}
            disabled={saving}
            data-testid="editor-save-draft-btn"
            className="border border-slate-300 bg-white px-6 py-2.5 font-display text-sm font-bold text-slate-700 transition-colors duration-150 hover:border-[#4687FF] disabled:opacity-60"
          >
            Save Draft
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            data-testid="editor-publish-btn"
            className="bg-[#4687FF] px-6 py-2.5 font-display text-sm font-bold text-white transition-colors duration-200 hover:bg-[#2563eb] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>

      <h1 className="mb-8 font-display text-2xl font-black tracking-tighter text-[#0F172A]">
        {isNew ? `New ${isPost ? "Blog Post" : "News Item"}` : `Edit: ${form.title}`}
      </h1>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Field label="Title *">
            <input data-testid="editor-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="e.g. Best VPN Deals for Black Friday 2026" />
          </Field>
          {isPost && (
            <Field label="Excerpt (shown on cards)">
              <textarea data-testid="editor-excerpt-input" rows={2} className={inputCls} value={form.excerpt} onChange={set("excerpt")} />
            </Field>
          )}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                {isPost ? "Content (Markdown)" : "Body (Markdown)"}
              </span>
              <button
                onClick={() => setPreview(!preview)}
                data-testid="editor-preview-toggle"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4687FF] hover:text-[#2563eb]"
              >
                {preview ? <PenLine size={13} /> : <Eye size={13} />} {preview ? "Edit" : "Live Preview"}
              </button>
            </div>
            {preview ? (
              <div className="min-h-[420px] border border-slate-200 bg-white p-8" data-testid="editor-preview-pane">
                <Markdown content={mdValue || "*Nothing to preview yet.*"} />
              </div>
            ) : (
              <textarea
                data-testid="editor-content-input"
                rows={20}
                className={`${inputCls} font-mono text-[13px] leading-relaxed`}
                value={mdValue}
                onChange={set(mdKey)}
                placeholder={"## Section heading\n\nYour paragraph text…\n\n- Bullet point\n\n[Get NordVPN](https://umtugxsg.com/click?trvid=56096)  ← affiliate links render as CTA buttons"}
              />
            )}
            <p className="mt-2 text-xs text-slate-400">
              Tip: use ## for headings, - for bullets, **bold**. Links to umtugxsg.com automatically render as blue CTA buttons.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {isPost && <AiAssistant form={form} setForm={setForm} />}
          <div className="border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">Settings</h3>
            <div className="space-y-4">
              {isPost ? (
                <Field label="Category">
                  <select data-testid="editor-category-select" className={inputCls} value={form.category} onChange={set("category")}>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="Tag">
                  <select data-testid="editor-tag-select" className={inputCls} value={form.tag} onChange={set("tag")}>
                    {TAGS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Slug (blank = auto from title)">
                <input data-testid="editor-slug-input" className={inputCls} value={form.slug || ""} onChange={set("slug")} placeholder="auto-generated" />
              </Field>
              <Field label="Date (blank = today)">
                <input data-testid="editor-date-input" type="date" className={inputCls} value={form.date || ""} onChange={set("date")} />
              </Field>
              {isPost && (
                <>
                  <Field label="Author">
                    <input className={inputCls} value={form.author} onChange={set("author")} />
                  </Field>
                  <Field label="Reading time">
                    <input className={inputCls} value={form.reading_time} onChange={set("reading_time")} />
                  </Field>
                </>
              )}
              {!isPost && (
                <>
                  <Field label="CTA label (optional)">
                    <input data-testid="editor-cta-label-input" className={inputCls} value={form.cta_label || ""} onChange={set("cta_label")} placeholder="Check out NordVPN" />
                  </Field>
                  <Field label="CTA URL (optional)">
                    <input data-testid="editor-cta-url-input" className={inputCls} value={form.cta_url || ""} onChange={set("cta_url")} placeholder="https://umtugxsg.com/click?trvid=56096" />
                  </Field>
                </>
              )}
            </div>
          </div>
          {isPost && (
            <div className="border border-slate-200 bg-white p-6">
              <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-500">SEO</h3>
              <div className="space-y-4">
                <Field label="Meta title (blank = title)">
                  <input className={inputCls} value={form.meta_title || ""} onChange={set("meta_title")} />
                </Field>
                <Field label="Meta description (blank = excerpt)">
                  <textarea rows={3} className={inputCls} value={form.meta_description || ""} onChange={set("meta_description")} />
                </Field>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminEditor;
